/**
 * Debug logs for unexpected shadedMesh.scale changes during locking socket align.
 * Enable: default in dev. Disable: localStorage.setItem('lockingSocketScaleDebug', '0')
 * Force on:  localStorage.setItem('lockingSocketScaleDebug', '1')
 */

const PREFIX = '[LockingSocketScale]';

const _lastScaleByMesh = new WeakMap();

export function isLockingSocketScaleDebugEnabled() {
  if (typeof localStorage !== 'undefined') {
    const flag = localStorage.getItem('lockingSocketScaleDebug');
    if (flag === '0') return false;
    if (flag === '1') return true;
  }
  return import.meta.env?.DEV !== false;
}

export function meshScaleSnapshot(mesh) {
  if (!mesh?.scale) return null;
  const { x, y, z } = mesh.scale;
  return { x, y, z };
}

function scalesEqual(a, b, eps = 1e-6) {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.x - b.x) <= eps &&
    Math.abs(a.y - b.y) <= eps &&
    Math.abs(a.z - b.z) <= eps
  );
}

/** Log info event (always when debug on). */
export function logLockingSocketScale(event, detail = {}) {
  if (!isLockingSocketScaleDebugEnabled()) return;
  console.log(PREFIX, event, detail);
}

/** Warn when mesh.scale differs from last snapshot for this mesh. */
export function trackMeshScale(mesh, event, context = {}) {
  if (!isLockingSocketScaleDebugEnabled() || !mesh) return meshScaleSnapshot(mesh);

  const cur = meshScaleSnapshot(mesh);
  const prev = _lastScaleByMesh.get(mesh);

  if (prev && !scalesEqual(prev, cur)) {
    console.warn(PREFIX, 'scale changed', {
      event,
      prev,
      cur,
      meshUuid: mesh.uuid,
      meshName: mesh.name,
      ...context,
      stack: new Error().stack,
    });
  }

  _lastScaleByMesh.set(mesh, { ...cur });
  return cur;
}

/** Log if scale changed between explicit before/after snapshots. */
export function logScaleIfChanged(event, mesh, before, context = {}) {
  if (!isLockingSocketScaleDebugEnabled() || !mesh) return;
  const after = meshScaleSnapshot(mesh);
  if (before && !scalesEqual(before, after)) {
    console.warn(PREFIX, 'scale mutated in operation', {
      event,
      before,
      after,
      meshUuid: mesh.uuid,
      ...context,
      stack: new Error().stack,
    });
  } else {
    logLockingSocketScale(event, {
      scale: after,
      unchanged: true,
      ...context,
    });
  }
  trackMeshScale(mesh, event, context);
}
